import { FeatureAnnouncement } from '../components/FeatureAnnouncement'

/**
 * Helper functions to create common feature announcements
 */

// Detect OS and return appropriate keyboard shortcut
const getKeyboardShortcut = (): string => {
  if (typeof window === 'undefined') return 'Ctrl+K'
  
  const platform = navigator.platform.toLowerCase()
  const userAgent = navigator.userAgent.toLowerCase()
  
  if (platform.includes('mac') || userAgent.includes('mac')) {
    return 'Cmd+K'
  }
  
  return 'Ctrl+K'
}

/**
 * Create a command palette announcement banner
 */
export const createCommandPaletteAnnouncement = (): FeatureAnnouncement => ({
  id: 'command-palette-intro',
  type: 'banner',
  title: '(New Feature) Quick Navigation',
  message: `Press ${getKeyboardShortcut()} to quickly find sidebar menu items`,
  color: 'info',
  dismissible: true,
  persistent: false, // Show once, then dismiss
})

/**
 * Create a feature announcement for new features
 */
export const createFeatureAnnouncement = (
  id: string,
  title: string,
  message: string,
  options?: {
    color?: 'info' | 'success' | 'warning' | 'error'
    actionLabel?: string
    onAction?: () => void
    persistent?: boolean
  }
): FeatureAnnouncement => ({
  id,
  type: 'banner',
  title,
  message,
  color: options?.color || 'info',
  dismissible: true,
  persistent: options?.persistent || false,
  actionLabel: options?.actionLabel,
  onAction: options?.onAction,
})

/**
 * Create a badge announcement (small chip/badge)
 */
export const createBadgeAnnouncement = (
  id: string,
  title: string,
  message: string,
  options?: {
    color?: 'info' | 'success' | 'warning' | 'error'
    onAction?: () => void
  }
): FeatureAnnouncement => ({
  id,
  type: 'badge',
  title,
  message,
  color: options?.color || 'primary',
  onAction: options?.onAction,
})

/**
 * Create an inline announcement (within content area)
 */
export const createInlineAnnouncement = (
  id: string,
  message: string,
  options?: {
    color?: 'info' | 'success' | 'warning' | 'error'
    dismissible?: boolean
  }
): FeatureAnnouncement => ({
  id,
  type: 'inline',
  message,
  color: options?.color || 'info',
  dismissible: options?.dismissible !== false,
})

